import { t } from '@lingui/core/macro'
import { pdf } from '@react-pdf/renderer'
import { message } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { PDFDocument } from 'features/reporting/pdfGeneration/PDFDocument'
import { useContextBridge } from 'its-fine'
import { RugFormDataState } from 'pages/reporting/reports/rug/rugReportData'
import {
  RugReportContext,
  useRugReportOps,
} from 'pages/reporting/reports/rug/RugReportProvider'
import { getAddendumFingerprint } from 'pages/reporting/reports/rug/utils/addendums/addendumContent'
import { composeAddendumPdfs } from 'pages/reporting/reports/rug/utils/addendums/attachmentPdf'
import { buildRugReport } from 'pages/reporting/reports/rug/utils/addendums/buildRugReport'
import { prepareAddendums } from 'pages/reporting/reports/rug/utils/addendums/prepareAddendums'
import { preparePdfHeatmaps } from 'pages/reporting/reports/rug/utils/preparePdfHeatmaps'
import { useSignedPdfFetcher } from 'pages/reporting/shared/eSignature/useSignedPdfFetcher'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { downloadBlob } from 'utils/download'
import { fetchImageAsBase64 } from 'utils/imageUtils'

interface GeneratedDraft {
  blob: Blob
  previewUrl: string
}

type TrackedPdfPage = {
  id: string
  pageNumber: number
}

export interface DraftPdfGeneration {
  isGenerating: boolean
  progress: number
  currentStep: string
  /**
   * Renders and uploads a new draft. Pass form data that was just saved so
   * the PDF reflects it without waiting for the provider to re-render.
   */
  generateDraft: (formDataOverride?: Partial<RugFormDataState>) => Promise<void>
  downloadCurrentDraft: () => Promise<void>
  previewCurrentDraft: () => void
}

/** Generates the draft PDF reviewers sign, keeping the just-generated bytes for instant preview/download. */
export function useDraftPdfGeneration(): DraftPdfGeneration {
  const contextValue = useRugReportOps()
  const {
    handleDownloadLatestDraft,
    handlePreviewLatestDraft,
    report,
    metadataEntryId,
    inspections,
    forceRefreshPdf,
    assetPicturesLoading,
    setIsDraftGenerating,
  } = contextValue

  const [isGenerating, setIsGenerating] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState('')
  const [latestGeneratedDraft, setLatestGeneratedDraft] =
    useState<GeneratedDraft>()
  const initialSectionPagesRef = useRef<Record<string, number>>({})
  const generationRef = useRef<AbortController | undefined>(undefined)
  const lastTimestampRef = useRef(0)
  const { rolodexUploadFile, rolodexPatch } = useRolodexEdit()
  const { fetchFileBytes } = useSignedPdfFetcher()

  const Bridge = useContextBridge()

  const reportSectionTitleById = useMemo(() => {
    return [
      report.coverSection,
      report.tocSection,
      ...(report.sections ?? []),
      ...(report.staticSections ?? []),
    ].reduce<Record<string, string>>((acc, section) => {
      if (section) {
        acc[section.id] = section.title
      }
      return acc
    }, {})
  }, [
    report.coverSection,
    report.sections,
    report.staticSections,
    report.tocSection,
  ])

  const handleTrackedPageRender = useCallback(
    ({ id, pageNumber }: TrackedPdfPage) => {
      initialSectionPagesRef.current = {
        ...initialSectionPagesRef.current,
        [id]: pageNumber,
      }

      setProgress((prev) => Math.min(prev + 8, 90))
      setCurrentStep(`Generating ${reportSectionTitleById[id] ?? id} page...`)
    },
    [reportSectionTitleById]
  )

  const generatePDF = useCallback(
    async (
      formDataOverride: Partial<RugFormDataState> | undefined,
      signal: AbortSignal
    ) => {
      setProgress(0)
      setCurrentStep('Preparing PDF document...')
      setProgress(20)

      const snapshot = { ...contextValue.formData, ...formDataOverride }
      const addendums = snapshot.sectionAddendums ?? []
      const prepared = await prepareAddendums(
        addendums,
        {
          fetchBytes: (path, signal) =>
            fetchFileBytes(metadataEntryId, path, signal),
          encodeImage: async (bytes, signal) => {
            const url = URL.createObjectURL(new Blob([bytes]))
            try {
              return await fetchImageAsBase64(url, signal)
            } finally {
              URL.revokeObjectURL(url)
            }
          },
        },
        signal
      )
      const snapshotReport = formDataOverride?.sectionAddendums
        ? buildRugReport(addendums)
        : report
      setCurrentStep(t`Preparing PDF images...`)
      const preparedHeatmaps = await preparePdfHeatmaps(
        contextValue.assetScreenshots,
        signal
      )
      try {
        const pdfContextValue = {
          ...contextValue,
          report: snapshotReport,
          formData: snapshot,
          assetScreenshots: preparedHeatmaps.screenshots,
          addendumImageSources: prepared.imageSources,
        }
        initialSectionPagesRef.current = {}

        // Both passes use the same prepared snapshot; the first discovers TOC page numbers.
        const renderPdf = () =>
          pdf(
            <Bridge>
              <RugReportContext.Provider value={pdfContextValue}>
                <PDFDocument
                  report={snapshotReport}
                  onPageRender={handleTrackedPageRender}
                  initialSectionPages={initialSectionPagesRef.current}
                />
              </RugReportContext.Provider>
            </Bridge>
          ).toBlob()

        setCurrentStep('Initializing PDF...')
        await renderPdf()
        signal.throwIfAborted()
        setProgress(20)

        setCurrentStep('Finalizing PDF...')
        const blob = await renderPdf()
        signal.throwIfAborted()
        const composed = prepared.pdfSources.size
          ? await composeAddendumPdfs(blob, prepared.pdfSources)
          : blob
        signal.throwIfAborted()
        return {
          blob: composed,
          fingerprint: getAddendumFingerprint(addendums),
          recordDraft: addendums.length > 0 || Boolean(snapshot.addendumDraft),
        }
      } finally {
        preparedHeatmaps.dispose()
      }
    },
    [
      contextValue,
      report,
      Bridge,
      handleTrackedPageRender,
      fetchFileBytes,
      metadataEntryId,
    ]
  )

  const generateDraft = useCallback(
    async (formDataOverride?: Partial<RugFormDataState>) => {
      if (generationRef.current) return
      if (
        contextValue.formData?.revisionMetadata?.isPublished ||
        contextValue.formData?.publishedPdfPath ||
        contextValue.reportSignatures?.isBusy ||
        contextValue.isSavingAddendums ||
        contextValue.formData?.eSignatures?.records.length
      ) {
        message.info(
          'Void active signatures before regenerating an unpublished report'
        )
        return
      }
      if (assetPicturesLoading) {
        message.info(
          'Regenerate the draft PDF once report images finish preparing'
        )
        return
      }
      const controller = new AbortController()
      generationRef.current = controller
      setIsDraftGenerating?.(true)
      try {
        setIsGenerating(true)
        const generated = await generatePDF(formDataOverride, controller.signal)
        const pdfBlob = generated.blob

        const timestampMillis = Math.max(
          Date.now(),
          lastTimestampRef.current + 1000
        )
        lastTimestampRef.current = timestampMillis
        const d = new Date(timestampMillis)
        const pad = (n: number) => String(n).padStart(2, '0')
        const timestamp = `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}-${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}`
        if (inspections && inspections.length > 0) {
          const inspectionIds = inspections.map((i) => i.id).join('-')
          const path = `reportPDFDrafts/rug-report-${inspectionIds}-${metadataEntryId}-draft-${timestamp}.pdf`
          await rolodexUploadFile(metadataEntryId, pdfBlob, path)
          controller.signal.throwIfAborted()
          if (generated.recordDraft) {
            const addendumDraft = { path, fingerprint: generated.fingerprint }
            await rolodexPatch([
              {
                id: metadataEntryId,
                data: { section_info: { addendumDraft } },
              },
            ])
            controller.signal.throwIfAborted()
            contextValue.updateFormData({ addendumDraft })
          }
          setLatestGeneratedDraft({
            blob: pdfBlob,
            previewUrl: window.URL.createObjectURL(pdfBlob),
          })
        }

        setProgress(100)
        message.success('PDF generated successfully')
        await forceRefreshPdf().catch(() =>
          message.warning(
            'Draft saved, but the draft list could not be refreshed. Reload Review to try again.'
          )
        )
      } catch (error) {
        if (!controller.signal.aborted)
          message.error(
            error instanceof Error ? error.message : 'Failed to generate PDF'
          )
      } finally {
        if (generationRef.current === controller) {
          generationRef.current = undefined
          setIsDraftGenerating?.(false)
          setIsGenerating(false)
          setProgress(0)
          setCurrentStep('')
        }
      }
    },
    [
      assetPicturesLoading,
      generatePDF,
      inspections,
      metadataEntryId,
      rolodexUploadFile,
      forceRefreshPdf,
      rolodexPatch,
      contextValue,
      setIsDraftGenerating,
    ]
  )

  useEffect(
    () => () => {
      if (latestGeneratedDraft) {
        window.URL.revokeObjectURL(latestGeneratedDraft.previewUrl)
      }
    },
    [latestGeneratedDraft]
  )

  // Review remains mounted while the user switches revisions; in-flight drafts belong to their original revision.
  useEffect(() => {
    setLatestGeneratedDraft(undefined)
    setIsGenerating(false)
    return () => {
      generationRef.current?.abort()
      generationRef.current = undefined
      setIsDraftGenerating?.(false)
    }
  }, [metadataEntryId, setIsDraftGenerating])

  const downloadCurrentDraft = useCallback(async () => {
    if (latestGeneratedDraft) {
      downloadBlob(latestGeneratedDraft.blob, 'inspection-report-draft.pdf')
      return
    }

    await handleDownloadLatestDraft()
  }, [latestGeneratedDraft, handleDownloadLatestDraft])

  const previewCurrentDraft = useCallback(() => {
    handlePreviewLatestDraft(latestGeneratedDraft?.previewUrl)
  }, [handlePreviewLatestDraft, latestGeneratedDraft])

  return {
    isGenerating,
    progress,
    currentStep,
    generateDraft,
    downloadCurrentDraft,
    previewCurrentDraft,
  }
}
